import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';

import { NavigationToggle, PageInfo, ViewerAsset } from '@models/viewer-asset';
import { ButtonComponent } from '@shared/button/button.component';
import { MarkdownService } from '@shared/markdown.service';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { Subscription } from 'rxjs';
import { ViewerService } from '../viewer.service';

@Component({
  selector: 'zbp-viewer-navigation-toggle',
  templateUrl: './viewer-navigation-toggle.component.html',
  styleUrls: ['./viewer-navigation-toggle.component.scss'],
  imports: [
    ButtonComponent
  ]
})
export class ViewerNavigationToggleComponent implements OnInit, OnChanges, OnDestroy {
  viewerAssets: ViewerAsset[] = [];
  previousDescription: string;
  previousDescriptionText: string;
  nextDescription: string;
  nextDescriptionText: string;
  navAnnouncement: string;
  @Input() pageInfo: PageInfo;
  @Input() navToggle: NavigationToggle;
  @Output() pageChanged = new EventEmitter<string>();

  viewerAssetsSubscription: Subscription;

  protected readonly zbpIconNames = zbpIconNames;

  constructor(private router: Router,
              private viewerService: ViewerService,
              private markdown: MarkdownService,) {
  }

  ngOnInit(): void {
    this.viewerAssetsSubscription = this.viewerService.viewerAssets$.subscribe((viewerAssets) => {
      if (viewerAssets?.length > 0) {
        this.viewerAssets = viewerAssets;
        this.setNavDescriptions();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.navToggle) {
      this.setNavDescriptions();
      this.updateNavAnnouncement(changes.navToggle.firstChange);
    }
  }

  get hasPrevious(): boolean {
    if (!this.navToggle || !this.viewerAssets?.length || this.pageInfo?.page === 1) return false;

    if (this.viewerAssets.length > 0 && this.navToggle?.previous) {
      return this.viewerAssets.some(viewerAsset => viewerAsset.assetId === this.navToggle.previous);
    }
    return !!this.navToggle?.previous;
  }

  get hasNext(): boolean {
    if (this.viewerAssets.length > 0 && this.navToggle?.next) {
      return this.viewerAssets.some(viewerAsset => viewerAsset.assetId === this.navToggle.next);
    }
    return !!this.navToggle?.next;
  }

  setNavDescriptions() {
    this.previousDescription = this.getNavDescription(this.navToggle.previous);
    let temp = new DOMParser().parseFromString(this.previousDescription, 'text/html');
    this.previousDescriptionText = temp.body.textContent || '';
    this.nextDescription = this.getNavDescription(this.navToggle.next);
    temp = new DOMParser().parseFromString(this.nextDescription, 'text/html');
    this.nextDescriptionText = temp.body.textContent || '';
  }

  getNavDescription(assetId: string): string {
    let asset = null;

    if (this.viewerAssets?.length > 0) {
      asset = this.viewerAssets.find(viewerAsset => viewerAsset.assetId === assetId);
    }

    return asset ? this.renderText(asset.name) : '-';
  }

  renderText(value: string): string {
    return this.markdown.renderInline(value);
  }

  toggleNext(): void {
    this.navigateToToggle(this.navToggle.next);
    this.pageChanged.emit(this.navToggle.next); // used for print-canvas viewer
  }

  togglePrevious(): void {
    this.navigateToToggle(this.navToggle.previous);
    this.pageChanged.emit(this.navToggle.previous); // used for print-canvas viewer
  }

  navigateToToggle(assetId: string): void {
    this.router.navigate([], {
      queryParams: {
        assetId
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  updateNavAnnouncement(isFirstChange = false): void {
    if (!isFirstChange && this.pageInfo?.page && this.pageInfo?.pages) {
      this.navAnnouncement = `Page ${this.pageInfo.page} of ${this.pageInfo.pages}`;
    } else if (!this.hasPrevious) {
      this.navAnnouncement = 'Previous material unavailable';
    } else if (!this.hasNext) {
      this.navAnnouncement = 'Next material unavailable';
    } else {
      this.navAnnouncement = '';
    }
  }

  ngOnDestroy(): void {
    this.viewerAssetsSubscription?.unsubscribe();
  }

}
